import BaseApiService from './BaseApiService.js'
import { authConfig, env } from '@/utils/env.js'

/**
 * Authentication API Service
 * Handles all authentication-related API operations
 */
class AuthService extends BaseApiService {
  constructor() {
    super('auth') // Laravel API endpoint for auth
  }

  /**
   * User login
   * @param {object} credentials - Login credentials
  * @param {string} credentials.login - Email, username, or phone
  * @param {string} [credentials.email] - Email address (legacy)
   * @param {string} credentials.password - Password
   * @returns {Promise} API response with user data and token
   */
  async login(credentials) {
    try {
      const payload = {
        login: credentials.login || credentials.email,
        password: credentials.password
      }
      
      const response = await this.post(payload, 'login')
      
      // Store token if login successful
      if (response.success && response.data?.token) {
        this.setAuthData(response.data.token, response.data.user)
        
        // Emit auth state change event
        window.dispatchEvent(new CustomEvent('auth:login', { 
          detail: { user: response.data.user } 
        }))
        
        env.log('User logged in successfully')
      }
      
      return response
    } catch (error) {
      // Clear any existing auth data on failed login
      this.clearAuthData()
      throw error
    }
  }

  /**
   * Exchange a public-client Keycloak access token for a Nawat Sanctum token.
   */
  async exchangeSsoToken(accessToken) {
    const response = await this.post({ access_token: accessToken }, 'sso/exchange')

    if (response.success && response.data?.token && response.data?.user) {
      this.setAuthData(response.data.token, response.data.user)
      window.dispatchEvent(new CustomEvent('auth:login', {
        detail: { user: response.data.user }
      }))
    }

    return response
  }

  /**
   * User logout
   * @returns {Promise} API response
   */
  async logout() {
    try {
      // Only attempt API logout if we have a token
      const token = this.getToken()
      
      if (token) {
        try {
          const response = await this.post({}, 'logout')
          env.debug('Logout API call successful')
          return response
        } catch (error) {
          // If API call fails, log but don't rethrow - we still want to clear local auth
          env.warn('Logout API call failed but continuing with local logout:', error.message)
        }
      } else {
        env.debug('No token found, skipping API logout call')
      }
      
      return { success: true, message: 'Logged out successfully' }
    } finally {
      // Always clear auth data
      this.clearAuthData()
      
      // Emit manual logout event but with a flag to prevent infinite loops
      window.dispatchEvent(new CustomEvent('auth:logout', { 
        detail: { reason: 'manual', source: 'authService' } 
      }))
    }
  }

  /**
   * Get current authenticated user
   * @returns {Promise} API response with current user data
   */
  async getCurrentUser() {
    return this.get('me')
  }

  /**
   * Update authenticated user's profile
   * @param {object} profileData - Profile data to update
   * @param {string} profileData.first_name - First name
   * @param {string} profileData.last_name - Last name
   * @param {string} profileData.email - Email address
   * @param {string} profileData.phone - Phone number
   * @param {string} profileData.username - Username
   * @returns {Promise} API response with updated user data
   */
  async updateProfile(profileData) {
    try {
      const response = await this.request('PUT', 'profile', profileData)

      if (response.success && response.data) {
        this.updateUserData(response.data)
        env.log('Profile updated successfully')
      }

      return response
    } catch (error) {
      env.error('Failed to update profile:', error)
      throw error
    }
  }

  /**
   * Upload user avatar
   * @param {File} file - The image file to upload
   * @returns {Promise} API response with updated user data
   */
  async uploadAvatar(file) {
    try {
      const formData = new FormData()
      formData.append('avatar', file)

      const response = await this.post(formData, 'profile/avatar')

      if (response.success && response.data) {
        this.updateUserData(response.data)
        env.log('Avatar uploaded successfully')
      }

      return response
    } catch (error) {
      env.error('Failed to upload avatar:', error)
      throw error
    }
  }

  /**
   * Remove user avatar
   * @returns {Promise} API response with updated user data
   */
  async removeAvatar() {
    try {
      const response = await this.request('DELETE', 'profile/avatar')

      if (response.success && response.data) {
        this.updateUserData(response.data)
        env.log('Avatar removed successfully')
      }

      return response
    } catch (error) {
      env.error('Failed to remove avatar:', error)
      throw error
    }
  }

  /**
   * Change user password
   * @param {object} passwordData - Password change data
   * @param {string} passwordData.current_password - Current password
   * @param {string} passwordData.new_password - New password
   * @param {string} passwordData.new_password_confirmation - New password confirmation
   * @returns {Promise} API response
   */
  async changePassword(passwordData) {
    try {
      const payload = {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password,
        new_password_confirmation: passwordData.new_password_confirmation
      }
      
      const response = await this.post(payload, 'change-password')
      env.log('Password changed successfully')
      return response
    } catch (error) {
      env.error('Failed to change password:', error)
      throw error
    }
  }

  /**
   * Consume a password-reset link and set a new password. Public endpoint.
   * @param {object} payload - Reset data
   * @param {string} payload.email - Account email (from the link's query params)
   * @param {string} payload.token - Reset token (from the link's query params)
   * @param {string} payload.password - New password
   * @param {string} payload.password_confirmation - New password confirmation
   * @returns {Promise} API response
   */
  async resetPassword(payload) {
    return this.post(payload, 'reset-password')
  }

  /**
   * Verify if user is authenticated
   * @returns {boolean} True if authenticated
   */
  isAuthenticated() {
    const token = this.getToken()
    const userData = this.getUserData()
    return !!(token && userData)
  }

  /**
   * Get stored auth token
   * @returns {string|null} Auth token or null
   */
  getToken() {
    const storage = env.getStorage()
    return storage.getItem(authConfig.tokenKey)
  }

  /**
   * Get stored user data
   * @returns {object|null} User data or null
   */
  getUserData() {
    const storage = env.getStorage()
    const userData = storage.getItem(authConfig.userDataKey)
    
    try {
      return userData ? JSON.parse(userData) : null
    } catch (error) {
      env.error('Failed to parse user data:', error)
      return null
    }
  }

  /**
   * Get stored user data (alias for getUserData)
   * @returns {object|null} User data or null
   */
  getUser() {
    return this.getUserData()
  }

  /**
   * Set authentication data in storage
   * @param {string} token - Auth token
   * @param {object} user - User data
   */
  setAuthData(token, user) {
    const storage = env.getStorage()
    storage.setItem(authConfig.tokenKey, token)
    storage.setItem(authConfig.userDataKey, JSON.stringify(user))
    env.debug('Auth data stored successfully')
  }

  /**
   * Clear authentication data from storage
   */
  clearAuthData() {
    const storage = env.getStorage()
    storage.removeItem(authConfig.tokenKey)
    storage.removeItem(authConfig.userDataKey)
    env.debug('Auth data cleared')
  }

  /**
   * Update stored user data
   * @param {object} userData - Updated user data
   */
  updateUserData(userData) {
    const storage = env.getStorage()
    storage.setItem(authConfig.userDataKey, JSON.stringify(userData))
    env.debug('User data updated')
  }

  /**
   * Check if user has specific role
   * @param {string} role - Role name to check
   * @returns {boolean} True if user has the role
   */
  hasRole(role) {
    const userData = this.getUserData()
    if (!userData || !userData.roles) return false
    
    return userData.roles.some(r => r.name === role)
  }

  /**
   * Check if user has specific permission
   * @param {string} permission - Permission name to check
   * @returns {boolean} True if user has the permission
   */
  hasPermission(permission) {
    const userData = this.getUserData()
    if (!userData || !userData.permissions) return false
    
    return userData.permissions.some(p => p.name === permission)
  }

  /**
   * Check if user has any of the specified roles
   * @param {array} roles - Array of role names
   * @returns {boolean} True if user has any of the roles
   */
  hasAnyRole(roles) {
    return roles.some(role => this.hasRole(role))
  }

  /**
   * Check if user has all of the specified roles
   * @param {array} roles - Array of role names
   * @returns {boolean} True if user has all of the roles
   */
  hasAllRoles(roles) {
    return roles.every(role => this.hasRole(role))
  }

  /**
   * Check if user has any of the specified permissions
   * @param {array} permissions - Array of permission names
   * @returns {boolean} True if user has any of the permissions
   */
  hasAnyPermission(permissions) {
    return permissions.some(permission => this.hasPermission(permission))
  }

  /**
   * Check if user has all of the specified permissions
   * @param {array} permissions - Array of permission names
   * @returns {boolean} True if user has all of the permissions
   */
  hasAllPermissions(permissions) {
    return permissions.every(permission => this.hasPermission(permission))
  }
}

// Export singleton instance
export default new AuthService()
